import React, { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Button, Form, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import UserService from "../services/user.service";
import { FaChartBar, FaCalendarAlt, FaMousePointer } from "react-icons/fa";

const COLORS = ["#2F7FF0", "#1fa971", "#FBBC04", "#e04646", "#7c5cbf", "#17a2b8"];

const formatDateToYYYYMMDD = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const CustomerClickBoard = ({ scope }) => {
  const own = scope === "own";
  const today = new Date();
  const [startDate, setStartDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [endDate, setEndDate] = useState(new Date(today.getFullYear(), today.getMonth() + 1, 0));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [activeTab, setActiveTab] = useState("uebersicht");

  const fetchFilteredData = async () => {
    setLoading(true);
    try {
      const payload = {
        startDate: formatDateToYYYYMMDD(startDate),
        endDate: formatDateToYYYYMMDD(endDate),
      };
      const response = own
        ? await UserService.customerRequestData(payload)
        : await UserService.customerERequestData(payload);
      const nameKey = own ? "customerCompanyName" : "employeeName";
      const rows = (response.data || []).map((item) => ({
        request_date: item.request_date,
        name: item[nameKey] || "ohne Name",
        batch_count: Number(item.batch_count) || 0,
      }));
      const grouped = {};
      rows.forEach((row) => {
        if (!grouped[row.request_date]) grouped[row.request_date] = { request_date: row.request_date };
        grouped[row.request_date][row.name] = (grouped[row.request_date][row.name] || 0) + row.batch_count;
      });
      const chartData = Object.values(grouped).filter((entry) =>
        Object.keys(entry).some((key) => key !== "request_date" && entry[key] > 0)
      );
      setData(chartData);
    } catch (error) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilteredData();
    const dataInterval = setInterval(() => {
      fetchFilteredData();
      setCountdown(60);
    }, 60000);
    const countdownInterval = setInterval(() => {
      setCountdown((previous) => (previous > 0 ? previous - 1 : 60));
    }, 1000);
    return () => {
      clearInterval(dataInterval);
      clearInterval(countdownInterval);
    };
  }, [startDate, endDate]);

  const series = useMemo(() => {
    const names = new Set();
    data.forEach((entry) => {
      Object.keys(entry).forEach((key) => {
        if (key !== "request_date") names.add(key);
      });
    });
    return Array.from(names);
  }, [data]);

  const total = data.reduce((sum, entry) => (
    sum + series.reduce((inner, name) => inner + (Number(entry[name]) || 0), 0)
  ), 0);

  const tabs = [
    { id: "uebersicht", label: "Übersicht", icon: <FaMousePointer aria-hidden="true" /> },
    { id: "diagramm", label: "Diagramm", icon: <FaChartBar aria-hidden="true" /> },
    { id: "zeitraum", label: "Zeitraum", icon: <FaCalendarAlt aria-hidden="true" /> },
  ];

  return (
    <section className="myn-sheet">
      <header className="myn-sheet__head">
        <span className="myn-sheet__icon" aria-hidden="true"><FaChartBar /></span>
        <div>
          <p className="myn-sheet__kicker">Statistik</p>
          <h1 className="myn-sheet__title">{own ? "Meine Klicks" : "Klicks meiner Mitarbeiter"}</h1>
          <p className="myn-sheet__lead">
            {own
              ? "Hier sehen Sie nur Ihre eigenen Klicks. Die Klicks Ihrer Mitarbeiter liegen auf der eigenen Seite."
              : "Hier sehen Sie nur die Klicks Ihrer Mitarbeiter. Ihre eigenen Klicks liegen auf der Seite Meine Klicks."}
            {" "}Aktualisierung in {countdown} Sekunden.
          </p>
        </div>
      </header>
      <div className="myn-sheet__tabs" role="tablist" aria-label="Klickauswertung">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={activeTab === tab.id ? "myn-sheet__tab is-active" : "myn-sheet__tab"}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {activeTab === "uebersicht" && (
        <div className="myn-sheet__panel">
          <div className="myn-sheet__grid">
            <article className="myn-sheet__tile"><FaMousePointer aria-hidden="true" /><span>Klicks im Zeitraum</span><strong>{total}</strong></article>
            <article className="myn-sheet__tile"><FaCalendarAlt aria-hidden="true" /><span>Tage mit Klicks</span><strong>{data.length}</strong></article>
            <article className="myn-sheet__tile"><FaChartBar aria-hidden="true" /><span>{own ? "Konten" : "Mitarbeiter"}</span><strong>{series.length}</strong></article>
          </div>
          <div className="myn-sheet__stack">
            {loading && <p className="myn-sheet__muted"><Spinner animation="border" size="sm" /> Wird geladen …</p>}
            {!loading && data.length === 0 && <p>Keine Daten vorhanden.</p>}
            {data.map((entry) => (
              <article className="myn-sheet__review" key={entry.request_date}>
                <h2>{entry.request_date}</h2>
                {series.filter((name) => entry[name]).map((name) => (
                  <p key={name} className="myn-sheet__link"><span>{name}</span><strong>{entry[name]} Klicks</strong></p>
                ))}
              </article>
            ))}
          </div>
        </div>
      )}

      {activeTab === "diagramm" && (
        <div className="myn-sheet__panel">
          {loading ? <p className="myn-sheet__muted"><Spinner animation="border" size="sm" /> Wird geladen …</p> : data.length === 0 ? <p>Keine Daten vorhanden.</p> : (
            <div className="myn-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="request_date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis allowDecimals={false} width={32} />
                  <Tooltip />
                  <Legend />
                  {series.map((name, index) => (
                    <Bar key={name} dataKey={name} fill={COLORS[index % COLORS.length]} name={name} maxBarSize={28} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {activeTab === "zeitraum" && (
        <div className="myn-sheet__panel">
          <Form className="myn-sheet__stack" onSubmit={(event) => { event.preventDefault(); fetchFilteredData(); }}>
            <Form.Group>
              <Form.Label>Startdatum</Form.Label>
              <DatePicker selected={startDate} onChange={(date) => date && setStartDate(date)} dateFormat="dd.MM.yyyy" className="form-control" withPortal />
            </Form.Group>
            <Form.Group>
              <Form.Label>Enddatum</Form.Label>
              <DatePicker selected={endDate} onChange={(date) => date && setEndDate(date)} dateFormat="dd.MM.yyyy" className="form-control" withPortal />
            </Form.Group>
            <Button className="myn-btn myn-btn--primary" type="submit" disabled={loading}>
              {loading ? "Wird geladen …" : "Zeitraum anwenden"}
            </Button>
          </Form>
        </div>
      )}
    </section>
  );
};

export default CustomerClickBoard;
